import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { adminPost } from "./adminApi";
import { callPostApi } from "../../function/ApiCallFunction";
import { DataContext } from "../../context/DataContext";
import { cashiLogo } from "../../include/cashiBrand";
import { indiaDateTime } from "../../include/indiaDate";

const COUNTDOWN_SECONDS = 10;
const SPIN_SECONDS = 20;

function nameCase(value) {
  return String(value || "").trim().toLowerCase().replace(/(^|\s|-)([a-z])/g, (_, sep, letter) => `${sep}${letter.toUpperCase()}`);
}


function maskMobile(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "—";
  const mobile = digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;
  if (mobile.length < 7) return `${mobile.slice(0, Math.min(3, mobile.length))}XXX`;
  return `${mobile.slice(0, 3)}XXX${mobile.slice(-4)}`;
}

function winnerWhen(winner) {
  return indiaDateTime(winner?.winner_at || winner?.at || winner?.date_added || "");
}

function waitForAudio(audio, ms = 8000) {
  return new Promise((resolve) => {
    if (!audio) return resolve();
    if (audio.readyState >= 3) return resolve();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      audio.removeEventListener("canplaythrough", finish);
      audio.removeEventListener("loadeddata", finish);
      audio.removeEventListener("error", finish);
      resolve();
    };
    audio.addEventListener("canplaythrough", finish);
    audio.addEventListener("loadeddata", finish);
    audio.addEventListener("error", finish);
    try { audio.load(); } catch { finish(); }
    setTimeout(finish, ms);
  });
}

function waitForImage(src, ms = 8000) {
  return new Promise((resolve) => {
    if (!src) return resolve();
    const img = new Image();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    img.onload = finish;
    img.onerror = finish;
    img.src = src;
    setTimeout(finish, ms);
  });
}

export default function LaunchDraw({ kind = "draw", source = "admin" }) {
  const { drawId, ticketId, token } = useParams();
  const id = kind === "ticket" ? ticketId : drawId;
  const [params] = useSearchParams();
  const slot = params.get("slot");
  const mode = params.get("mode") || "manual";
  const slotKey = params.get("key") || "";
  const navigate = useNavigate();
  const { loginData, clubData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const clubId = clubData?.club_info?.club_id;
  const [count, setCount] = useState(COUNTDOWN_SECONDS);
  const [armed, setArmed] = useState(false);
  const [phase, setPhase] = useState("load");
  const [winner, setWinner] = useState(null);
  const [others, setOthers] = useState([]);
  const [picking, setPicking] = useState(false);
  const spinSeconds = useRef(SPIN_SECONDS);
  const [entries, setEntries] = useState([]);
  const [reel, setReel] = useState(null);
  const [tick, setTick] = useState(0);
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [launchArt, setLaunchArt] = useState({ left: "", right: "" });
  const [payloadReady, setPayloadReady] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const [artKnown, setArtKnown] = useState(false);
  const entriesRef = useRef([]);
  const winnerRef = useRef(null);
  const audioRef = useRef(null);
  const audioDoneRef = useRef(false);
  entriesRef.current = entries;
  winnerRef.current = winner;

  const post = useCallback((mod, data) => (
    source === "store"
      ? callPostApi("store-draw", { mod, data_arr: { ...data, store_id: storeId } })
      : source === "club"
        ? callPostApi("club-panel", { mod, data_arr: { ...data, club_id: clubId } })
        : adminPost(mod, data)
  ), [source, storeId, clubId]);

  const launchBody = () => {
    const index = Number(slot);
    const ticketIndex = Number.isInteger(index) && index >= 0 ? index : 0;
    const frequencyLaunch = mode === "all" || mode === "frequency";
    if (kind === "ticket") return { ticket_id: id, result_mode: "manual", launch_index: ticketIndex };
    if (frequencyLaunch) return { draw_id: id, result_mode: mode === "all" ? "all" : "frequency", slot_key: slotKey };
    return { draw_id: id, result_mode: "manual", launch_index: index };
  };

  useEffect(() => {
    if (source === "public") return undefined;
    if (!id || (source === "store" && !storeId) || (source === "club" && !clubId)) return undefined;
    let alive = true;
    const launchKind = kind === "ticket" ? "ticket" : "coupon";
    const request = post("LAUNCH_IMAGE_FOR", { kind: launchKind, target_id: id });
    request.then((result) => {
      if (!alive) return;
      const rows = result?.data?.images || [];
      setLaunchArt({
        left: rows.find((row) => row.side === "left")?.image || "",
        right: rows.find((row) => row.side === "right")?.image || "",
      });
      setArtKnown(true);
    }).catch(() => { if (alive) setArtKnown(true); });
    return () => { alive = false; };
  }, [id, kind, source, storeId, clubId, post]);

  useEffect(() => {
    const applyReady = (result) => {
      if (result.status === "200" && (result.data?.entries?.length || result.data?.participants?.length)) {
        const people = result.data.entries?.length ? result.data.entries : result.data.participants;
        setCount(COUNTDOWN_SECONDS);
        spinSeconds.current = SPIN_SECONDS;
        const rows = result.data.images || [];
        setLaunchArt({
          left: rows.find((row) => row.side === "left")?.image || "",
          right: rows.find((row) => row.side === "right")?.image || "",
        });
        if (source === "public" || rows.length) setArtKnown(true);
        setEntries(people);
        setTitle(result.data.title || result.data.slot_label || "");
        setPayloadReady(true);
      } else {
        setError(result.status_message || result.data?.error || "Could not launch this draw");
        setPhase("error");
      }
    };

    if (source === "public") {
      if (!token) {
        setError("This launch link is not valid");
        setPhase("error");
        return undefined;
      }
      let alive = true;
      callPostApi("draw-launch", { mod: "DRAW_LAUNCH_OPEN", data_arr: { token } }).then((result) => {
        if (alive) applyReady(result);
      });
      return () => { alive = false; };
    }

    if ((source === "store" && !storeId) || (source === "club" && !clubId)) return undefined;
    const index = Number(slot);
    const ticketIndex = Number.isInteger(index) && index >= 0 ? index : 0;
    const frequencyLaunch = mode === "all" || mode === "frequency";
    if (kind !== "ticket" && !frequencyLaunch && (!Number.isInteger(index) || index < 0)) {
      setError("Choose which draw to launch");
      setPhase("error");
      return undefined;
    }
    if (frequencyLaunch && !slotKey) {
      setError("Choose a frequency date and time");
      setPhase("error");
      return undefined;
    }
    let alive = true;
    const mod = kind === "ticket" ? "TICKET_LAUNCH_READY" : "DRAW_LAUNCH_READY";
    const body = kind === "ticket"
      ? { ticket_id: id, result_mode: "manual", launch_index: ticketIndex }
      : frequencyLaunch
        ? { draw_id: id, result_mode: mode === "all" ? "all" : "frequency", slot_key: slotKey }
        : { draw_id: id, result_mode: "manual", launch_index: index };
    const request = post(mod, body);
    request.then((result) => {
      if (alive) applyReady(result);
    });
    return () => { alive = false; };
  }, [id, slot, mode, slotKey, kind, source, storeId, clubId, token, post]);

  useEffect(() => {
    if (!armed || phase !== "count") return undefined;
    if (count <= 0) {
      const next = setTimeout(() => setPhase((current) => (current === "error" ? current : "spin")), 1000);
      return () => clearTimeout(next);
    }
    const step = setTimeout(() => setCount((value) => value - 1), 1000);
    return () => clearTimeout(step);
  }, [armed, count, phase]);

  useEffect(() => {
    const existing = window.__cashiLaunchAudio;
    window.__cashiLaunchAudio = null;
    const audio = existing || new Audio(`${process.env.PUBLIC_URL || ""}/cashi.wav`);
    audio.preload = "auto";
    try { audio.pause(); audio.currentTime = 0; } catch { /* ignore */ }
    audioRef.current = audio;
    audioDoneRef.current = false;
    const done = () => { audioDoneRef.current = true; };
    audio.addEventListener("ended", done);
    audio.addEventListener("error", done);
    let alive = true;
    waitForAudio(audio).then(() => { if (alive) setAudioReady(true); });
    return () => {
      alive = false;
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!artKnown) return undefined;
    let alive = true;
    Promise.all([waitForImage(launchArt.left), waitForImage(launchArt.right)]).then(() => {
      if (alive) setImagesReady(true);
    });
    return () => { alive = false; };
  }, [artKnown, launchArt.left, launchArt.right]);

  useEffect(() => {
    if (phase !== "load" || !payloadReady || !audioReady || !imagesReady) return undefined;
    setPhase("ready");
    return undefined;
  }, [phase, payloadReady, audioReady, imagesReady]);

  const startNow = async () => {
    if (phase !== "ready" || picking) return;
    setPicking(true);
    setError("");
    try {
      const result = source === "public"
        ? await callPostApi("draw-launch", { mod: "DRAW_LAUNCH_PICK", data_arr: { token } })
        : await post(kind === "ticket" ? "TICKET_LAUNCH" : "DRAW_ISSUE_WINNER", launchBody());
      if (result.status !== "200" || !result.data?.winner) {
        setError(result.status_message || result.data?.error || "Could not pick a winner");
        return;
      }
      const people = result.data.entries?.length
        ? result.data.entries
        : (result.data.participants?.length ? result.data.participants : [result.data.winner]);
      setWinner(result.data.winner);
      setOthers(Array.isArray(result.data.winners) ? result.data.winners.slice(1) : []);
      setEntries(people);
      setCount(COUNTDOWN_SECONDS);
      spinSeconds.current = SPIN_SECONDS;
      const audio = audioRef.current;
      if (audio) {
        try { audio.currentTime = 0; } catch { /* ignore */ }
        audioDoneRef.current = false;
        audio.play().then(() => {}, () => {});
      }
      setArmed(true);
      setPhase("count");
    } catch {
      setError("Could not pick a winner");
    } finally {
      setPicking(false);
    }
  };

  useEffect(() => {
    if (phase !== "spin") return undefined;
    const started = Date.now();
    const timer = setInterval(() => {
      const list = entriesRef.current.length ? entriesRef.current : (winnerRef.current ? [winnerRef.current] : []);
      if (list.length) {
        setReel(list[Math.floor(Math.random() * list.length)]);
        setTick((value) => value + 1);
      }
      if (Date.now() - started >= spinSeconds.current * 1000 && winnerRef.current) {
        clearInterval(timer);
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          audioDoneRef.current = true;
        }
        setPhase((current) => (current === "error" ? current : "winner"));
      }
    }, 90);
    return () => clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== "winner" && phase !== "error") return undefined;
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audioDoneRef.current = true;
    }
    return undefined;
  }, [phase]);

  const back = () => navigate(source === "store" ? "/luckydraws" : source === "club" ? "/club/luckydraw" : kind === "ticket" ? "/console/tickets" : "/console/luckydraw");
  const fallbackTitle = kind === "ticket" ? "Lucky Draw Ticket" : "Lucky Draw Coupons";
  const tryPlay = () => {
    const audio = audioRef.current;
    if (!audio || audioDoneRef.current || !audio.paused || phase === "winner" || phase === "error") return;
    audio.play().then(() => {}, () => {});
  };

  return (
    <Box onPointerDown={tryPlay} sx={{
      position: "fixed",
      inset: 0,
      zIndex: 1400,
      overflow: "hidden",
      display: "flex",
      alignItems: "stretch",
      background: "radial-gradient(circle at 50% 20%, #14324a 0%, #070b10 55%, #05070a 100%)",
    }}>
      {phase !== "load" && <LaunchSide image={launchArt.left} />}
      <Box sx={{ flex: 1, minWidth: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
      <Glow />
      <Confetti on={phase === "winner"} />
      <Box component="img" src={cashiLogo} alt="Cashi" sx={{ position: "absolute", top: { xs: 18, sm: 28 }, left: "50%", transform: "translateX(-50%)", height: { xs: 88, sm: 86 }, width: "auto", zIndex: 2 }} />
      {source !== "public" && (
        <Box component="button" type="button" onClick={back} sx={{ position: "absolute", right: 18, bottom: 16, zIndex: 3, border: 0, background: "transparent", color: "#8fd4f8", fontSize: 13, fontWeight: 650, cursor: "pointer", p: 0 }}>Back</Box>
      )}
      {phase === "load" && (
        <Box sx={{ position: "relative", zIndex: 4, textAlign: "center", color: "#fff" }}>
          <Typography sx={{ fontSize: 13, letterSpacing: 4, fontWeight: 800, color: "#21a7ec" }}>LUCKY DRAW</Typography>
          <Box sx={{
            width: 72,
            height: 72,
            mx: "auto",
            mt: 2.5,
            borderRadius: "50%",
            border: "3px solid rgba(33,167,236,0.2)",
            borderTopColor: "#21a7ec",
            animation: "launch-load 0.8s linear infinite",
          }} />
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 650, mt: 2.5, color: "#d5dee8" }}>Loading...</Typography>
        </Box>
      )}
      {phase === "ready" && (
        <Box sx={{ position: "relative", zIndex: 4, textAlign: "center", color: "#fff" }}>
          <Typography sx={{ fontSize: 13, letterSpacing: 4, fontWeight: 800, color: "#21a7ec" }}>LUCKY DRAW</Typography>
          <Typography sx={{ fontSize: { xs: 22, sm: 28 }, fontWeight: 800, mt: 1.5, color: "#f8fbff" }}>{title || fallbackTitle}</Typography>
          <Box
            component="button"
            type="button"
            disabled={picking}
            onClick={startNow}
            sx={{
              mt: 3.5,
              border: 0,
              cursor: picking ? "default" : "pointer",
              color: "#fff",
              fontSize: { xs: 18, sm: 22 },
              fontWeight: 800,
              letterSpacing: 0.6,
              px: { xs: 4, sm: 5.5 },
              py: { xs: 1.4, sm: 1.7 },
              borderRadius: 999,
              opacity: picking ? 0.7 : 1,
              background: "linear-gradient(180deg, #2eb4f3 0%, #21a7ec 55%, #1788c9 100%)",
              boxShadow: "0 0 0 8px rgba(33,167,236,0.16), 0 12px 40px rgba(33,167,236,0.35)",
            }}
          >
            {picking ? "Picking..." : "Launch now"}
          </Box>
          {error && (
            <Typography sx={{ mt: 2, fontSize: 14, color: "#fb7185" }}>{error}</Typography>
          )}
        </Box>
      )}
      {phase === "error" && (
        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", color: "#fff", maxWidth: 520 }}>
          <Typography sx={{ fontSize: 13, letterSpacing: 3, fontWeight: 800, color: "#21a7ec" }}>LUCKY DRAW</Typography>
          <Typography sx={{ fontSize: { xs: 28, sm: 36 }, fontWeight: 850, mt: 1.5 }}>{error}</Typography>
        </Box>
      )}
      {phase === "count" && (
        <Box key={count} sx={{ position: "relative", zIndex: 1, textAlign: "center", color: "#fff", animation: "launch-pop 0.55s cubic-bezier(.2,1.4,.4,1)" }}>
          <Typography sx={{ fontSize: 13, letterSpacing: 4, fontWeight: 800, color: "#21a7ec" }}>LAUNCHING DRAW</Typography>
          <Box sx={{
            width: { xs: 220, sm: 280 },
            height: { xs: 220, sm: 280 },
            mx: "auto",
            mt: 2,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            border: "2px solid rgba(33,167,236,0.55)",
            boxShadow: "0 0 0 14px rgba(33,167,236,0.08), 0 0 80px rgba(33,167,236,0.35)",
            animation: "launch-pulse 1s ease",
          }}>
            <Typography sx={{ fontSize: { xs: 120, sm: 156 }, fontWeight: 900, lineHeight: 1, color: "#f8fbff", textShadow: "0 0 40px rgba(33,167,236,0.8)" }}>
              {armed ? count : "…"}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 650, mt: 2.5, color: "#d5dee8" }}>
            {count > 0 ? "Get ready" : "Drawing now"}
          </Typography>
        </Box>
      )}
      {phase === "spin" && (
        <Box key={tick} sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 640, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", color: "#fff", animation: "launch-slot 0.09s linear" }}>
          <Typography sx={{ fontSize: 13, letterSpacing: 4, fontWeight: 800, color: "#21a7ec" }}>DRAWING</Typography>
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, mt: 1, fontWeight: 650, color: "#d5dee8" }}>{title || fallbackTitle}</Typography>
          <Typography sx={{ fontSize: { xs: 40, sm: 58 }, fontWeight: 900, lineHeight: 1.05, mt: 2, minHeight: "1.1em" }}>{reel?.customer_name ? nameCase(reel.customer_name) : "…"}</Typography>
          <Box sx={{ mt: 2.5, display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.4, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 3, px: 4, py: 1.6, minWidth: 240 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: 2, fontWeight: 800, color: "#8fd4f8" }}>MOBILE</Typography>
            <Typography sx={{ fontSize: { xs: 26, sm: 34 }, fontWeight: 850, letterSpacing: 1 }}>{reel?.customer_mobile ? maskMobile(reel.customer_mobile) : "…"}</Typography>
          </Box>
        </Box>
      )}
      {phase === "winner" && !winner && (
        <Typography sx={{ position: "relative", zIndex: 1, color: "#fff", fontSize: 28, fontWeight: 800 }}>Picking the winner...</Typography>
      )}
      {phase === "winner" && winner && (
        <Box sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 640, maxHeight: "88vh", overflow: "auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", color: "#fff", animation: "launch-pop 0.6s cubic-bezier(.2,1.4,.4,1)" }}>
          <Typography sx={{ fontSize: 13, letterSpacing: 4, fontWeight: 800, color: "#21a7ec" }}>WINNER</Typography>
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, mt: 1, fontWeight: 650, color: "#d5dee8" }}>{title || fallbackTitle}</Typography>
          <Typography sx={{ fontSize: { xs: 42, sm: 64 }, fontWeight: 900, lineHeight: 1.05, mt: 2 }}>{nameCase(winner.customer_name)}</Typography>
          <Box sx={{ mt: 2.5, display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.4, background: "rgba(33,167,236,0.14)", border: "1px solid rgba(33,167,236,0.55)", borderRadius: 3, px: 4, py: 1.6 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: 2, fontWeight: 800, color: "#8fd4f8" }}>MOBILE</Typography>
            <Typography sx={{ fontSize: { xs: 28, sm: 36 }, fontWeight: 850 }}>{maskMobile(winner.customer_mobile)}</Typography>
          </Box>
          {winner.txnid && (
            <Typography sx={{ mt: 1.5, fontSize: 14, color: "#d5dee8" }}>Payment Transaction Id {winner.txnid}</Typography>
          )}
          {winnerWhen(winner) && (
            <Typography sx={{ mt: 1.6, fontSize: 16, fontWeight: 650, color: "#d5dee8" }}>{winnerWhen(winner)}</Typography>
          )}
          {others.length > 0 && (
            <Typography sx={{ mt: 1.2, fontSize: 14, color: "#8fd4f8", maxWidth: 480 }}>
              Also picked: {others.map((item) => nameCase(item.customer_name)).join(", ")}
            </Typography>
          )}
        </Box>
      )}
      <style>{`
        @keyframes launch-pop {
          0% { transform: scale(0.6); opacity: 0; }
          70% { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes launch-slot {
          0% { transform: translateY(18px); filter: blur(8px); opacity: 0.35; }
          100% { transform: translateY(0); filter: blur(0); opacity: 1; }
        }
        @keyframes launch-pulse {
          0% { transform: scale(0.86); }
          100% { transform: scale(1); }
        }
        @keyframes launch-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(105vh) rotate(240deg); opacity: 0.2; }
        }
        @keyframes launch-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes launch-load {
          to { transform: rotate(360deg); }
        }
      `}</style>
      </Box>
      {phase !== "load" && <LaunchSide image={launchArt.right} />}
    </Box>
  );
}

function LaunchSide({ image }) {
  if (!image) return null;
  return (
    <Box sx={{ height: "100%", flex: "0 0 auto", display: "flex", alignItems: "stretch", maxWidth: "38%" }}>
      <Box component="img" src={image} alt="" sx={{ height: "100%", width: "auto", maxWidth: "100%", objectFit: "contain", display: "block" }} />
    </Box>
  );
}

function Glow() {
  return (
    <Box sx={{
      position: "absolute",
      width: 520,
      height: 520,
      borderRadius: "50%",
      border: "1px dashed rgba(33,167,236,0.35)",
      animation: "launch-spin 18s linear infinite",
      pointerEvents: "none",
    }} />
  );
}

function Confetti({ on }) {
  if (!on) return null;
  const colors = ["#21a7ec", "#f8fbff", "#fde047", "#fb7185", "#34d399", "#c084fc"];
  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {colors.concat(colors, colors, colors).map((color, index) => (
        <Box key={index} sx={{
          position: "absolute",
          top: -16,
          left: `${(index * 17) % 100}%`,
          width: index % 3 === 0 ? 12 : 8,
          height: index % 2 === 0 ? 16 : 8,
          borderRadius: index % 2 ? "50%" : "2px",
          background: color,
          animation: `launch-fall ${2.2 + (index % 5) * 0.28}s linear ${(index % 7) * 0.08}s infinite`,
        }} />
      ))}
    </Box>
  );
}
